import React, { useState, useEffect, useRef } from 'react';

const CarouselSlider = ({
  slides = [],
  defaultSlides = [
    {
      id: 1,
      name: 'Sally Sha',
      role: 'Marketing',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      description: 'Lorem ipsum dolor sit adipisicing elit. lus',
    },
    {
      id: 2,
      name: 'Michael John',
      role: 'Cybersecurity Engineer',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      description: 'Lorem ipsum dolor sit amet consecte adipisicing elit. Dolorem, dolore.',
    },
    {
      id: 3,
      name: 'Mikayla Eddie',
      role: 'Software Engineer',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      description: 'Lorem ipsum dolor sit amet consectetur, adipisicing elit. Consequatur, temporibus?',
    },
    {
      id: 4,
      name: 'Eve Smith',
      role: 'UI/UX Designer',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
      description: 'em ipsum dolor sit amet, consectetur adipisicing elit. Quod, beatae?',
    },
    {
      id: 5,
      name: 'Maxwell',
      role: 'Architect',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      description: 'or sit amet, consectetur lit. Esse, tempore.',
    },
  ],
  title = '',
  useFullScreen = true,
}) => {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const trackRef = useRef(null);

  const slidesToShow = slides.length > 0 ? slides : defaultSlides;
  const total = slidesToShow.length;

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get indices for prev, current, next (desktop only)
  const getIndex = (offset) => (current + offset + total) % total;
  const prevIdx = getIndex(-1);
  const nextIdx = getIndex(1);

  const goPrev = () => setCurrent(prev => (prev - 1 + total) % total);
  const goNext = () => setCurrent(prev => (prev + 1) % total);
  const goToSlide = (index) => setCurrent(index);

  // Touch handlers for swipe
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) goNext();
    if (isRightSwipe) goPrev();
  };

  // Lightbox functions
  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const lightboxPrev = () => {
    const newIndex = (lightboxIndex - 1 + total) % total;
    setLightboxIndex(newIndex);
    setLightboxImage(slidesToShow[newIndex].image);
  };

  const lightboxNext = () => {
    const newIndex = (lightboxIndex + 1) % total;
    setLightboxIndex(newIndex);
    setLightboxImage(slidesToShow[newIndex].image);
  };

  return (
    <>
      <div className="carousel-premium-container">
        {title && <h2 className="carousel-premium-title">{title}</h2>}

        <div
          className="carousel-premium-wrapper"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation Arrows */}
          <button
            className="carousel-premium-arrow carousel-premium-arrow-left"
            onClick={goPrev}
            aria-label="Previous"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="carousel-premium-track" ref={trackRef}>
            {isMobile ? (
              // Mobile: Single card view
              <div className="carousel-premium-card carousel-premium-card-active">
                <div className="carousel-premium-image-wrapper">
                  <img
                    className="carousel-premium-img"
                    src={slidesToShow[current].image}
                    alt={slidesToShow[current].name || `Slide ${current + 1}`}
                    onClick={useFullScreen ? () => openLightbox(slidesToShow[current].image, current) : undefined}
                    style={{ cursor: useFullScreen ? 'pointer' : 'default' }}
                    loading="lazy"
                  />
                  {useFullScreen && (
                    <div className="carousel-premium-overlay">
                      <button
                        className="carousel-premium-expand"
                        onClick={() => openLightbox(slidesToShow[current].image, current)}
                        aria-label="Expand image"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {(slidesToShow[current].name || slidesToShow[current].description) && (
                  <div className="carousel-premium-info">
                    {slidesToShow[current].name && <h3>{slidesToShow[current].name}</h3>}
                    {slidesToShow[current].description && <p>{slidesToShow[current].description}</p>}
                  </div>
                )}
              </div>
            ) : (
              // Desktop: Three card view
              <>
                <div className="carousel-premium-card carousel-premium-card-side carousel-premium-card-prev">
                  <div className="carousel-premium-image-wrapper">
                    <img
                      className="carousel-premium-img"
                      src={slidesToShow[prevIdx].image}
                      alt={slidesToShow[prevIdx].name || `Slide ${prevIdx + 1}`}
                      onClick={useFullScreen ? () => openLightbox(slidesToShow[prevIdx].image, prevIdx) : undefined}
                      style={{ cursor: useFullScreen ? 'pointer' : 'default' }}
                      loading="lazy"
                    />
                  </div>
                  {(slidesToShow[prevIdx].name || slidesToShow[prevIdx].description) && (
                    <div className="carousel-premium-info">
                      {slidesToShow[prevIdx].name && <h3>{slidesToShow[prevIdx].name}</h3>}
                      {slidesToShow[prevIdx].description && <p>{slidesToShow[prevIdx].description}</p>}
                    </div>
                  )}
                </div>

                <div className="carousel-premium-card carousel-premium-card-active carousel-premium-card-center">
                  <div className="carousel-premium-image-wrapper">
                    <img
                      className="carousel-premium-img"
                      src={slidesToShow[current].image}
                      alt={slidesToShow[current].name || `Slide ${current + 1}`}
                      onClick={useFullScreen ? () => openLightbox(slidesToShow[current].image, current) : undefined}
                      style={{ cursor: useFullScreen ? 'pointer' : 'default' }}
                      loading="lazy"
                    />
                    {useFullScreen && (
                      <div className="carousel-premium-overlay">
                        <button
                          className="carousel-premium-expand"
                          onClick={() => openLightbox(slidesToShow[current].image, current)}
                          aria-label="Expand image"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  {(slidesToShow[current].name || slidesToShow[current].description) && (
                    <div className="carousel-premium-info">
                      {slidesToShow[current].name && <h3>{slidesToShow[current].name}</h3>}
                      {slidesToShow[current].description && <p>{slidesToShow[current].description}</p>}
                    </div>
                  )}
                </div>

                <div className="carousel-premium-card carousel-premium-card-side carousel-premium-card-next">
                  <div className="carousel-premium-image-wrapper">
                    <img
                      className="carousel-premium-img"
                      src={slidesToShow[nextIdx].image}
                      alt={slidesToShow[nextIdx].name || `Slide ${nextIdx + 1}`}
                      onClick={useFullScreen ? () => openLightbox(slidesToShow[nextIdx].image, nextIdx) : undefined}
                      style={{ cursor: useFullScreen ? 'pointer' : 'default' }}
                      loading="lazy"
                    />
                  </div>
                  {(slidesToShow[nextIdx].name || slidesToShow[nextIdx].description) && (
                    <div className="carousel-premium-info">
                      {slidesToShow[nextIdx].name && <h3>{slidesToShow[nextIdx].name}</h3>}
                      {slidesToShow[nextIdx].description && <p>{slidesToShow[nextIdx].description}</p>}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <button
            className="carousel-premium-arrow carousel-premium-arrow-right"
            onClick={goNext}
            aria-label="Next"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Dots Indicator */}
        {total > 1 && (
          <div className="carousel-premium-dots">
            {slidesToShow.map((_, index) => (
              <button
                key={index}
                className={`carousel-premium-dot ${index === current ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Lightbox Modal */}
        {useFullScreen && lightboxOpen && (
          <div className="carousel-premium-lightbox-overlay" onClick={closeLightbox}>
            <div className="carousel-premium-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="carousel-premium-lightbox-close" onClick={closeLightbox} aria-label="Close">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <button
                className="carousel-premium-lightbox-nav carousel-premium-lightbox-prev"
                onClick={lightboxPrev}
                aria-label="Previous image"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              <div className="carousel-premium-lightbox-image-container">
                <img
                  className="carousel-premium-lightbox-image"
                  src={lightboxImage}
                  alt={`Lightbox ${lightboxIndex + 1}`}
                />
                <div className="carousel-premium-lightbox-counter">
                  {lightboxIndex + 1} / {total}
                </div>
              </div>

              <button
                className="carousel-premium-lightbox-nav carousel-premium-lightbox-next"
                onClick={lightboxNext}
                aria-label="Next image"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .carousel-premium-container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 60px 20px;
          position: relative;
        }

        .carousel-premium-title {
          text-align: center;
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          font-weight: 800;
          margin-bottom: 3rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.5px;
        }

        .carousel-premium-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 20px 0;
        }

        .carousel-premium-track {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          width: 100%;
          max-width: 1200px;
          position: relative;
          overflow: visible;
        }

        .carousel-premium-card {
          background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 24px;
          padding: 20px;
          box-shadow: 
            0 10px 40px rgba(0, 0, 0, 0.08),
            0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          border: 2px solid transparent;
        }

        .carousel-premium-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }

        .carousel-premium-card-active {
          z-index: 3;
          transform: scale(1);
          border-color: rgba(102, 126, 234, 0.3);
          box-shadow: 
            0 20px 60px rgba(102, 126, 234, 0.2),
            0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .carousel-premium-card-active::before {
          opacity: 1;
        }

        .carousel-premium-card-side {
          z-index: 1;
          opacity: 0.6;
          transform: scale(0.85);
          filter: blur(2px);
        }

        .carousel-premium-card-side:hover {
          opacity: 0.8;
          transform: scale(0.9);
          filter: blur(1px);
        }

        .carousel-premium-image-wrapper {
          position: relative;
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
          margin-bottom: 1.5rem;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .carousel-premium-image-wrapper:hover {
          transform: translateY(-4px);
        }

        .carousel-premium-img {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
          border-radius: 16px;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .carousel-premium-card-active .carousel-premium-img {
          transform: scale(1.02);
        }

        .carousel-premium-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          border-radius: 16px;
        }

        .carousel-premium-image-wrapper:hover .carousel-premium-overlay {
          opacity: 1;
        }

        .carousel-premium-expand {
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #667eea;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .carousel-premium-expand:hover {
          transform: scale(1.1);
          background: white;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
        }

        .carousel-premium-info {
          text-align: center;
          padding: 0 10px;
        }

        .carousel-premium-info h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.5rem;
          line-height: 1.3;
        }

        .carousel-premium-info p {
          font-size: 0.95rem;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        .carousel-premium-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          color: #475569;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        .carousel-premium-arrow:hover {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          color: white;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
        }

        .carousel-premium-arrow:active {
          transform: translateY(-50%) scale(0.95);
        }

        .carousel-premium-arrow-left {
          left: -28px;
        }

        .carousel-premium-arrow-right {
          right: -28px;
        }

        .carousel-premium-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 2.5rem;
          flex-wrap: wrap;
        }

        .carousel-premium-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: #cbd5e1;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
          touch-action: manipulation;
        }

        .carousel-premium-dot:hover {
          background: #94a3b8;
          transform: scale(1.2);
        }

        .carousel-premium-dot.active {
          width: 32px;
          height: 12px;
          border-radius: 6px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        /* Lightbox Styles */
        .carousel-premium-lightbox-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: lightboxFadeIn 0.3s ease-out;
        }

        .carousel-premium-lightbox-content {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          max-width: 95vw;
          max-height: 95vh;
        }

        .carousel-premium-lightbox-image-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: lightboxScaleIn 0.4s ease-out;
        }

        .carousel-premium-lightbox-image {
          max-width: 90vw;
          max-height: 85vh;
          object-fit: contain;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          animation: lightboxImageLoad 0.5s ease-out;
        }

        .carousel-premium-lightbox-counter {
          position: absolute;
          bottom: -50px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          color: white;
          padding: 10px 20px;
          border-radius: 24px;
          font-size: 15px;
          font-weight: 600;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .carousel-premium-lightbox-close {
          position: absolute;
          top: 30px;
          right: 30px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: white;
          transition: all 0.3s ease;
          z-index: 10001;
        }

        .carousel-premium-lightbox-close:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.1) rotate(90deg);
        }

        .carousel-premium-lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: white;
          transition: all 0.3s ease;
          z-index: 10001;
        }

        .carousel-premium-lightbox-nav:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-50%) scale(1.1);
        }

        .carousel-premium-lightbox-prev {
          left: 30px;
        }

        .carousel-premium-lightbox-next {
          right: 30px;
        }

        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes lightboxScaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes lightboxImageLoad {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .carousel-premium-card {
            padding: 16px;
          }
          
          .carousel-premium-arrow-left {
            left: -20px;
          }
          
          .carousel-premium-arrow-right {
            right: -20px;
          }
        }

        @media (max-width: 968px) {
          .carousel-premium-container {
            padding: 40px 16px;
          }
          
          .carousel-premium-track {
            gap: 1.5rem;
          }
          
          .carousel-premium-card {
            padding: 14px;
          }
          
          .carousel-premium-arrow {
            width: 48px;
            height: 48px;
          }
          
          .carousel-premium-arrow-left {
            left: -16px;
          }
          
          .carousel-premium-arrow-right {
            right: -16px;
          }
        }

        @media (max-width: 768px) {
          .carousel-premium-container {
            padding: 30px 12px;
          }
          
          .carousel-premium-title {
            margin-bottom: 2rem;
          }
          
          .carousel-premium-wrapper {
            padding: 10px 0;
          }
          
          .carousel-premium-track {
            gap: 0;
            justify-content: center;
          }
          
          .carousel-premium-card {
            width: 100%;
            max-width: 500px;
            padding: 16px;
            margin: 0 auto;
          }
          
          .carousel-premium-card-side {
            display: none;
          }
          
          .carousel-premium-card-active {
            transform: scale(1);
            opacity: 1;
            filter: none;
          }
          
          .carousel-premium-image-wrapper {
            margin-bottom: 1rem;
          }
          
          .carousel-premium-info h3 {
            font-size: 1.1rem;
          }
          
          .carousel-premium-info p {
            font-size: 0.9rem;
          }
          
          .carousel-premium-arrow {
            width: 44px;
            height: 44px;
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid #e2e8f0;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          }
          
          .carousel-premium-arrow-left {
            left: 8px;
          }
          
          .carousel-premium-arrow-right {
            right: 8px;
          }
          
          .carousel-premium-arrow svg {
            width: 20px;
            height: 20px;
          }
          
          .carousel-premium-dots {
            margin-top: 2rem;
            gap: 10px;
          }
          
          .carousel-premium-dot {
            width: 10px;
            height: 10px;
          }
          
          .carousel-premium-dot.active {
            width: 28px;
            height: 10px;
          }
        }

        @media (max-width: 480px) {
          .carousel-premium-container {
            padding: 24px 8px;
          }
          
          .carousel-premium-card {
            padding: 12px;
            border-radius: 20px;
          }
          
          .carousel-premium-image-wrapper {
            border-radius: 12px;
          }
          
          .carousel-premium-img {
            border-radius: 12px;
          }
          
          .carousel-premium-info h3 {
            font-size: 1rem;
          }
          
          .carousel-premium-info p {
            font-size: 0.85rem;
          }
          
          .carousel-premium-arrow {
            width: 40px;
            height: 40px;
          }
          
          .carousel-premium-arrow-left {
            left: 4px;
          }
          
          .carousel-premium-arrow-right {
            right: 4px;
          }
          
          .carousel-premium-arrow svg {
            width: 18px;
            height: 18px;
          }
          
          .carousel-premium-expand {
            width: 48px;
            height: 48px;
          }
          
          .carousel-premium-lightbox-close {
            top: 15px;
            right: 15px;
            width: 48px;
            height: 48px;
          }
          
          .carousel-premium-lightbox-nav {
            width: 52px;
            height: 52px;
          }
          
          .carousel-premium-lightbox-prev {
            left: 15px;
          }
          
          .carousel-premium-lightbox-next {
            right: 15px;
          }
          
          .carousel-premium-lightbox-image {
            max-width: 95vw;
            max-height: 80vh;
          }
          
          .carousel-premium-lightbox-counter {
            bottom: -40px;
            font-size: 13px;
            padding: 8px 16px;
          }
        }

        @media (max-width: 360px) {
          .carousel-premium-container {
            padding: 20px 6px;
          }
          
          .carousel-premium-card {
            padding: 10px;
          }
          
          .carousel-premium-arrow {
            width: 36px;
            height: 36px;
          }
          
          .carousel-premium-arrow svg {
            width: 16px;
            height: 16px;
          }
        }
      `}</style>
    </>
  );
};

export default CarouselSlider;
